import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { designSystem, reactHookForm } from '@yola/ws-ui';
import LinkTypeField from '../fields/link-type-field';
import AnchorField from '../fields/anchor-field';
import ExternalField from '../fields/external-field';
import PhoneField from '../fields/phone-field';
import MailField from '../fields/mail-field';
import PageField from '../fields/page-field';
import DownloadField from '../fields/download-field';
import BindingField from '../fields/binding-field';
import useBlocksOptions from '../hooks/use-blocks-options';
import usePagesOptions from '../hooks/use-pages-options';
import useDownloadCustomPath from '../hooks/use-download-custom-path';
import getLinkTypeOptions from '../helpers/get-link-type-options';
import fieldNames from '../constants/field-names';
import linkTypes from '../constants/link-types';

const { Box } = designSystem;
const { useWatch } = reactHookForm;

function LinkFieldContainer({
  control,
  types,
  initialLinkConfig,
  menuPlacement = 'auto',
  getFormValues,
  setFormValue,
  clearFormErrors,
  onLinkTypeChange,
  onFieldChange,
  onFieldClick,
}) {
  const linkType = useWatch({
    control,
    name: fieldNames.LINK_TYPE,
  });

  const linkTypeOptions = useMemo(() => getLinkTypeOptions(types), [types]);

  const handleCustomPathLoaded = useCallback(
    (customPath) => {
      setFormValue(fieldNames.DOWNLOAD_CUSTOM_PATH, customPath);
      onFieldChange({
        fieldName: fieldNames.DOWNLOAD_CUSTOM_PATH,
        fieldValue: customPath,
      });
    },
    [setFormValue, onFieldChange]
  );

  const { blocksOptions } = useBlocksOptions(initialLinkConfig);
  const { pagesOptions } = usePagesOptions();
  const downloadCustomPath = useDownloadCustomPath(initialLinkConfig, handleCustomPathLoaded);

  const handleForcePageReferenceChange = useCallback(() => {
    const pageReferenceValue = getFormValues(fieldNames.PAGE_REFERENCE);

    if (pageReferenceValue) {
      return;
    }

    setFormValue(fieldNames.PAGE_REFERENCE, pagesOptions[0].value, { shouldDirty: true });
    onFieldChange({
      fieldName: fieldNames.PAGE_REFERENCE,
      fieldValue: pagesOptions[0].value,
    });
  }, [getFormValues, pagesOptions, setFormValue, onFieldChange]);

  const handleLinkTypeChange = useCallback(
    (newLinkType) => {
      if (newLinkType.value === linkTypes.PAGE) {
        handleForcePageReferenceChange();
      }

      // Enable custom path section for DOWNLOAD
      if (newLinkType.value === linkTypes.DOWNLOAD) {
        downloadCustomPath.handlers.enableCustomPath();
      }

      clearFormErrors(Object.values(fieldNames));
      onLinkTypeChange(newLinkType);
    },
    [handleForcePageReferenceChange, onLinkTypeChange, downloadCustomPath.handlers, clearFormErrors]
  );

  return (
    <div>
      <LinkTypeField
        control={control}
        options={linkTypeOptions}
        menuPlacement={menuPlacement}
        onChange={handleLinkTypeChange}
      />
      <Box marginTop="spacing-3-xs">
        {linkType === linkTypes.ANCHOR && (
          <AnchorField
            control={control}
            options={blocksOptions}
            menuPlacement={menuPlacement}
            getFormValues={getFormValues}
            setFormValue={setFormValue}
            onChange={onFieldChange}
          />
        )}
        {linkType === linkTypes.EXTERNAL && (
          <ExternalField control={control} onChange={onFieldChange} onClick={onFieldClick} />
        )}
        {linkType === linkTypes.PHONE && (
          <PhoneField control={control} onChange={onFieldChange} onClick={onFieldClick} />
        )}
        {linkType === linkTypes.MAIL && (
          <MailField control={control} onChange={onFieldChange} onClick={onFieldClick} />
        )}
        {linkType === linkTypes.PAGE && (
          <PageField
            control={control}
            options={pagesOptions}
            menuPlacement={menuPlacement}
            setFormValue={setFormValue}
            onChange={onFieldChange}
          />
        )}
        {linkType === linkTypes.DOWNLOAD && (
          <DownloadField
            control={control}
            downloadCustomPath={downloadCustomPath}
            getFormValues={getFormValues}
            setFormValue={setFormValue}
            clearFormErrors={clearFormErrors}
            onChange={onFieldChange}
            onClick={onFieldClick}
          />
        )}
        {linkType === linkTypes.BINDING && (
          <BindingField
            control={control}
            getFormValues={getFormValues}
            setFormValue={setFormValue}
            onChange={onFieldChange}
            onClick={onFieldClick}
          />
        )}
      </Box>
    </div>
  );
}

LinkFieldContainer.propTypes = {
  control: PropTypes.shape({}).isRequired,
  types: PropTypes.arrayOf(PropTypes.oneOf(Object.values(linkTypes))).isRequired,
  initialLinkConfig: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.oneOf([null])]),
  menuPlacement: PropTypes.string,
  getFormValues: PropTypes.func.isRequired,
  setFormValue: PropTypes.func.isRequired,
  clearFormErrors: PropTypes.func.isRequired,
  onLinkTypeChange: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onFieldClick: PropTypes.func,
};

export default LinkFieldContainer;
